import {useMemo} from 'react';
import {TODO_STATUS} from '../../../core/entities/entryTodos/constants';
import {
  type EntryTodoType,
  useGetEntryTodos,
} from '../../../core/repositories/entryTodosRepository';
import {checkHasTodosForEntryManagement, filterTodos} from './todoUtils';

type TodoSortParams = {
  sortBy: 'position' | 'dueDate' | 'priority';
  sortOrder: 'asc' | 'desc';
};

/**
 * 検索候補オプションの型定義
 */
export type SearchOption = {
  id: string;
  value: string;
  type: 'company' | 'todo';
  originalName: string;
  companyId?: number;
  userEntryManagementID?: number | null;
};

/**
 * デフォルトのソートパラメータ（カスタム順）
 * 降順で最新のposition値が上位に表示される
 */
const DEFAULT_SORT_PARAMS: TodoSortParams = {
  sortBy: 'position',
  sortOrder: 'desc',
};

type UseEntryTodosDataArgs = {
  sortParams?: TodoSortParams;
  searchWord?: string;
  /** 認証済みかどうか（未ログイン時はデータフェッチを無効化） */
  isAuthenticated: boolean;
};

/**
 * TODOデータの取得・統合・フィルタリングを一括で行うカスタムフック
 *
 * @param sortParams - ソートパラメータ（デフォルト: 手動並び替え順）
 * @param searchWord - 検索ワード（デフォルト: 空文字）
 */
export const useTodosData = ({
  sortParams = DEFAULT_SORT_PARAMS,
  searchWord = '',
  isAuthenticated,
}: UseEntryTodosDataArgs) => {
  const {data, isLoading, isError, refetch} = useGetEntryTodos({
    sortBy: sortParams.sortBy,
    sortOrder: sortParams.sortOrder,
    isAuthenticated,
    config: {
      enabled: isAuthenticated,
    },
  });

  // APIレスポンスから全てのTODOを統合
  const allTodos = useMemo(() => {
    if (!data?.ok) return [];
    return [...data.value.todo, ...data.value.doing, ...data.value.done];
  }, [data]);

  // 検索候補リストの生成（エントリー管理単位の企業名 + ToDo名）
  const searchOptions = useMemo(() => {
    // エントリー管理ID単位でグループ化（同じ企業でも複数エントリー管理があれば別々に表示）
    const uniqueEntryManagements = new Map<
      number,
      {companyName: string; companyId: number}
    >();
    const uniqueTodoTitles = new Set<string>();

    for (const todo of allTodos) {
      // companyId=0でも企業名があれば含める、userEntryManagementIDでグループ化
      if (todo.company?.companyName && todo.userEntryManagementID != null) {
        uniqueEntryManagements.set(todo.userEntryManagementID, {
          companyName: todo.company.companyName,
          companyId: todo.company.companyID ?? 0,
        });
      }
      if (todo.title) {
        uniqueTodoTitles.add(todo.title);
      }
    }

    const options: SearchOption[] = Array.from(
      uniqueEntryManagements.entries(),
    ).map(([entryManagementId, {companyName, companyId}]) => ({
      id: `entry-${entryManagementId}`,
      value: `[企業] ${companyName}`,
      type: 'company' as const,
      originalName: companyName,
      companyId,
      userEntryManagementID: entryManagementId,
    }));

    options.push(
      ...Array.from(uniqueTodoTitles).map((title) => ({
        id: `todo-${title}`,
        value: `[ToDo] ${title}`,
        type: 'todo' as const,
        originalName: title,
      })),
    );

    return options;
  }, [allTodos]);

  // 検索ワードでフィルタリング
  const {filteredTodos, selectedEntryManagementId} = useMemo(() => {
    if (!searchWord) {
      return {filteredTodos: allTodos, selectedEntryManagementId: null};
    }

    const selectedOption = searchOptions.find(
      (opt) => opt.value === searchWord,
    );

    if (selectedOption) {
      if (selectedOption.type === 'company') {
        // エントリー管理IDでフィルタリング
        const entryManagementId = selectedOption.userEntryManagementID;
        return {
          filteredTodos: allTodos.filter(
            (todo) => todo.userEntryManagementID === entryManagementId,
          ),
          selectedEntryManagementId: entryManagementId ?? null,
        };
      }
      return {
        filteredTodos: filterTodos(
          allTodos,
          'todo',
          selectedOption.originalName,
        ),
        selectedEntryManagementId: null,
      };
    }

    return {
      filteredTodos: filterTodos(allTodos, 'partial', searchWord),
      selectedEntryManagementId: null,
    };
  }, [allTodos, searchWord, searchOptions]);

  // ステータス別のTODOとMapを生成
  const {todoTodos, doneTodos, todoMap} = useMemo(() => {
    const map = new Map<number, EntryTodoType>();
    const todo: EntryTodoType[] = [];
    const done: EntryTodoType[] = [];

    for (const item of filteredTodos) {
      map.set(item.id, item);
      if (
        item.status === TODO_STATUS.TODO ||
        item.status === TODO_STATUS.DOING
      ) {
        todo.push(item);
      } else if (item.status === TODO_STATUS.DONE) {
        done.push(item);
      }
    }

    return {todoTodos: todo, doneTodos: done, todoMap: map};
  }, [filteredTodos]);

  // ステータスに応じたTODO配列を取得するヘルパー関数
  const getTodosByStatus = (status: string): EntryTodoType[] => {
    switch (status) {
      case TODO_STATUS.TODO:
      case TODO_STATUS.DOING:
        return todoTodos;
      case TODO_STATUS.DONE:
        return doneTodos;
      default:
        return [];
    }
  };

  /**
   * 指定したentryManagementIdに該当するTODOが存在するかを判定
   */
  const hasTodosForEntryManagement = (
    entryManagementId: number | null,
  ): boolean => {
    return checkHasTodosForEntryManagement(allTodos, entryManagementId);
  };

  return {
    // データ取得状態
    isLoading,
    isError,
    refetch,
    // フィルタリング結果
    allTodos,
    searchOptions,
    todoTodos,
    doneTodos,
    todoMap,
    getTodosByStatus,
    selectedEntryManagementId,
    // ユーティリティ
    hasTodosForEntryManagement,
  };
};
